/**
 * v129 — Single source of truth for every safety + legal disclaimer surface in
 * The Forge.
 *
 * Why one file: copy drifts when it lives in six places. Sal asked
 * (in the v129 spec) for one canonical location so future edits are a single
 * pull request and the language stays consistent.
 *
 * Edit policy: Sal owns the text. Wording follows the Sal-voice rules locked
 * in v116/v120 (first-person, plainspoken, opinionated, no copywriter voice).
 * If you find a typo, fix it here and only here.
 *
 * SURFACES THAT CONSUME THIS FILE
 * ────────────────────────────────
 *   1. WelcomeModal.tsx (onboarding, localStorage gate)
 *   2. SafetyNote.tsx (inline at top of tuning steps — Paper / Bareshaft /
 *      Broadhead / Cam Timing)
 *   3. FinalReportStep.tsx + LaunchSummary (pre-shoot reminder above
 *      share/export buttons)
 *   4. WhenToSeekProfessionalHelp card (used on /pages/safety + expandable on
 *      tuning surfaces)
 *   5. StepShell footer link ("Safety & Legal Notes")
 *   6. /pages/safety (Shopify page — same copy, rendered server-side)
 */

/** The single short-form master disclaimer used on the Launch view and other
    "before you shoot" surfaces. Plain prose; consuming components decide markup. */
export const MASTER_DISCLAIMER =
  'The Forge calibrates pin positions, sight tape, and trajectory data to your ' +
  'specific setup (FPS, arrow weight, BC, peep-to-pin distance). If you change ' +
  'components, recalibrate. If you have never shot this setup live, verify at a ' +
  'known distance with field points before committing to broadhead work or hunting. ' +
  '\n\n' +
  'The Forge is precise guidance based on established archery practice and ' +
  'manufacturer specifications — but archery setup involves real risk. Verify ' +
  'unfamiliar steps with a qualified bow technician or pro shop. Sparrow ' +
  'Expeditions assumes no liability for outcomes resulting from use of this tool.';

/** Onboarding-modal copy (first-time use + major-version bumps). */
export const WELCOME_DISCLAIMER = {
  header: 'Welcome to The Forge',
  body: [
    'The Forge is a workshop, not a calculator. The math is reliable and we show ' +
    'our work on the Methods page — but archery setup involves stored energy, ' +
    'sharp edges, and real risk.',
    'Use The Forge with hands-on judgment. Verify any unfamiliar step with a ' +
    'qualified bow technician or pro shop. Improper setup, tuning, or cam work ' +
    'can damage equipment or cause injury. Sparrow Expeditions provides The ' +
    'Forge as guidance based on manufacturer specifications and established ' +
    'archery practice; we assume no liability for outcomes resulting from use ' +
    'of this tool.',
  ],
  primaryCta: 'I understand — let me start building',
  secondaryLinkLabel: 'Read full Safety & Legal Notes',
  secondaryHref: '/pages/safety',
};

/** Per-operation inline safety notes. Severity drives SafetyNote.tsx styling. */
export const SAFETY_NOTES = {
  paperTune: {
    severity: 'caution' as const,
    title: 'Safety note · paper tuning',
    body: 'Use real broadheads only on a dedicated broadhead target — they can cut ' +
      'through standard target faces and ricochet. Wear eye protection. Stand square ' +
      'to the target with a clear backstop. If your arrow group hits unexpected ' +
      'positions, stop and verify spine + rest position before continuing.',
  },
  bareshaft: {
    severity: 'caution' as const,
    title: 'Safety note · extended bareshaft',
    body: 'Bareshafts at 50–70 yd require a private range with a confident, ' +
      'controlled shooter. Bareshafts can fly unpredictably under wind or operator ' +
      'error. Never shoot bareshafts on a public range or where a back-stop isn\'t ' +
      'guaranteed at the full distance. Stop if you can\'t confirm impact every shot.',
  },
  broadhead: {
    severity: 'caution' as const,
    title: 'Safety note · broadhead tuning',
    body: 'Broadheads are dramatically sharper than field points. Handle by the ' +
      'ferrule, not the blades. Use a broadhead-rated target. Wear eye protection. ' +
      'If you\'re new to broadheads, set up at a closer distance first to confirm ' +
      'consistent flight before stepping back to your hunting distances.',
  },
  camTiming: {
    severity: 'critical' as const,
    title: 'Safety note · cam timing',
    body: 'Only adjust cam timing if you are comfortable with a bow press and ' +
      'understand the risks. Modern compounds store substantial energy at draw — a ' +
      'slipped cable, twisted cam, or improperly pressed limb can damage the bow ' +
      'and seriously injure the operator. If you don\'t own a verified bow press, ' +
      'take this work to a qualified pro shop. When in doubt, don\'t.',
  },
};

/** Pre-shoot reminder block — shown above share/export on Launch view. */
export const PRE_SHOOT_REMINDER = {
  header: 'Before you shoot this setup:',
  body: MASTER_DISCLAIMER,
};

/** "When to seek professional help" — full text. Used on /pages/safety and as
    an expandable card surfaced contextually from tuning surfaces. */
export const SEEK_PROFESSIONAL_HELP = {
  title: 'When to take it to a pro shop',
  intro: 'Even archers comfortable with their own setup should pause for any of these:',
  bullets: [
    'Visible limb cracks, twists, separations, or surface damage',
    'String or cable fraying, separation, stretching beyond spec, or visible servings breaking down',
    'Cam timing or cam lean issues you can\'t isolate to one cause',
    'Any work that requires a bow press if you don\'t own one and trust it',
    'First setup on a new bow if you didn\'t build it yourself or watch the pro do it',
    'When a tune isn\'t responding the way the math says it should, after you\'ve ruled out arrows',
    'Anything that feels off — trust your instinct',
  ],
  outro: 'Sparrow Expeditions doesn\'t operate a pro shop. We build the tools and ' +
    'document the methods. For hands-on work, find a qualified bow technician — ' +
    'most regional pro shops have one, and they\'re worth what they charge.',
};

/** The localStorage key gating the welcome modal.
 *
 * v132.1 — Pegged to the DISCLAIMER TEXT, not the code version. Code-version
 * bumps no longer re-prompt; only a material change to the disclaimer copy
 * does. Bump the `disclaimer-vN` suffix when (and only when) the user-facing
 * acknowledgment language in this file changes meaningfully.
 *
 * Migration: any user who acknowledged under the legacy `sparrowForgeWelcomeAck.v129`
 * key is treated as already-acked by `WelcomeModal.tsx` so v129/130/131/132
 * users don't re-prompt. New ack writes always go to the new key. */
export const WELCOME_ACK_STORAGE_KEY = 'sparrowForgeWelcomeAck.disclaimer-v1';

/** Legacy keys that, if present, count as a valid ack and suppress the modal.
 *  Add (don't remove) entries here when bumping the primary key so existing
 *  users aren't re-prompted unnecessarily. */
export const LEGACY_WELCOME_ACK_KEYS: readonly string[] = [
  'sparrowForgeWelcomeAck.v129', // shipped v129 → v132 inclusive
];

/** Footer link label used in StepShell + anywhere else "Safety & Legal Notes"
    appears as navigation. */
export const SAFETY_LINK = {
  label: 'Safety & Legal Notes',
  href: '/pages/safety',
};
